import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const story = [
  {
    title: "A real question arrives.",
    copy: "Website, Messenger, or Instagram. The conversation begins wherever the customer already feels comfortable.",
    visual: <div className="story-question"><span>Customer</span><p>Which option works for a team of four?</p></div>,
  },
  {
    title: "The answer uses business context.",
    copy: "The system retrieves approved knowledge, follows the business voice, and gives a clear next action instead of a vague reply.",
    visual: <div className="story-answer"><img src="/brand/marky-solid.svg" alt="" /><p>The Growth plan is designed for a four-person team. I can show you what is included.</p></div>,
  },
  {
    title: "Interest becomes structured context.",
    copy: "Contact details, intent, channel, and the relevant part of the conversation stay together for the next step.",
    visual: <div className="story-fields"><span><small>Interested in</small><strong>Growth plan</strong></span><span><small>Team size</small><strong>Four people</strong></span><span><small>Next step</small><strong>Talk with the team</strong></span></div>,
  },
  {
    title: "A person joins without a restart.",
    copy: "The handoff includes what the customer wants, what has already been answered, and why the team is stepping in.",
    visual: <div className="story-handoff"><div><span>MA</span><span>KL</span></div><p><strong>Ready for a human handoff</strong>Full conversation and contact context included.</p></div>,
  },
] as const;

export function ConversationStory() {
  const root = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const context = gsap.context(() => {
      const media = gsap.matchMedia();
      media.add("(min-width: 901px) and (prefers-reduced-motion: no-preference)", () => {
        const panels = gsap.utils.toArray<HTMLElement>("[data-story-panel]");
        const dots = gsap.utils.toArray<HTMLElement>("[data-story-dot]");
        gsap.set(panels.slice(1), { autoAlpha: 0, y: 44, scale: 0.975 });
        gsap.set(dots.slice(1), { opacity: 0.28, scale: 0.75 });

        const timeline = gsap.timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            trigger: root.current,
            start: "top top",
            end: "+=2400",
            pin: true,
            scrub: 0.65,
            anticipatePin: 1,
          },
        });

        for (let index = 1; index < panels.length; index += 1) {
          const at = index * 0.78;
          timeline
            .to(panels[index - 1], { autoAlpha: 0, y: -36, scale: 1.02, duration: 0.22 }, at)
            .fromTo(panels[index], { autoAlpha: 0, y: 44, scale: 0.975 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.28 }, at + 0.03)
            .to(dots[index - 1], { opacity: 0.28, scale: 0.75, duration: 0.16 }, at)
            .to(dots[index], { opacity: 1, scale: 1, duration: 0.16 }, at + 0.1);
        }
      });
      return () => media.revert();
    }, root);
    return () => context.revert();
  }, []);

  return (
    <section className="conversation-story" ref={root} aria-labelledby="conversation-story-title">
      <div className="story-header">
        <p id="conversation-story-title">Follow one useful conversation.</p>
        <div aria-hidden="true">{story.map((item, index) => <i key={item.title} data-story-dot className={index === 0 ? "is-active" : ""} />)}</div>
        <span>Question to human handoff</span>
      </div>
      <div className="story-stage">
        {story.map((item, index) => (
          <article key={item.title} data-story-panel className={index === 0 ? "is-first" : ""}>
            <div className="story-copy">
              <span>{String(index + 1).padStart(2, "0")} / 04</span>
              <h2>{item.title}</h2>
              <p>{item.copy}</p>
            </div>
            <div className="story-visual">
              <div className="story-window-top"><span>Hello Marky Chat</span><small>Product walkthrough</small></div>
              <div className="story-window-body">{item.visual}</div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
